import { Facebook, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useSite, waLinkFor } from "@/lib/SiteContext";
import { ChapterHeading, Em, Reveal } from "./primitives";

export default function WhatsAppCTA({ content }) {
  const { contact } = useSite();
  const c = content || {};

  const rows = [
    { icon: Phone, label: "Call / WhatsApp", value: contact.phoneDisplay || contact.phone_display, href: `tel:+${(contact.whatsapp || "").replace(/\D/g, "")}`, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}`, testid: "contact-email" },
    { icon: Instagram, label: "Instagram", value: `@${contact.instagram}`, href: contact.instagramUrl || contact.instagram_url, testid: "contact-instagram" },
    { icon: Facebook, label: "Facebook", value: contact.facebook, href: contact.facebookUrl || contact.facebook_url, testid: "contact-facebook" },
    { icon: MapPin, label: "Studio", value: contact.address, href: contact.mapsUrl || contact.maps_url, testid: "contact-address" },
  ].filter((r) => r.value);

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading
          number={(c.kicker || "07 — Contact").split("—")[0].trim() || "07"}
          kicker={(c.kicker || "07 — Contact").split("—")[1]?.trim() || "Contact"}
          title={<Em text={c.title || "Tell Us About *Your Moment*"} />}
          blurb={c.text}
        />
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <h3 className="font-serif text-3xl font-medium leading-tight text-ink md:text-4xl">
              <Em text={c.heading || "Prefer to chat? *We're one tap away.*"} />
            </h3>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-[#5C5C5C] md:text-base">
              {c.text ||
                "Message us on WhatsApp with your occasion and budget — we will curate options, personalize them and deliver across India."}
            </p>
            <a
              data-testid="whatsapp-order-button"
              href={waLinkFor(contact)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#25D366] px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/20 transition-colors duration-300 hover:bg-[#1eb857]"
            >
              <MessageCircle size={18} /> {c.button_label || "Chat on WhatsApp"}
            </a>
            <p data-testid="payment-note" className="mt-4 text-xs leading-relaxed text-[#5C5C5C]">
              {c.payment_note || contact.payment_note}
            </p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-6">
            <ul className="flex flex-col gap-6 rounded-[32px] border border-line bg-white p-8 shadow-xl shadow-rose/5 md:p-10">
              {rows.map((row) => (
                <li key={row.label}>
                  <a
                    data-testid={row.testid}
                    href={row.href}
                    target={row.href?.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-cream text-rose transition-colors duration-300 group-hover:bg-rose group-hover:text-white">
                      <row.icon size={17} />
                    </span>
                    <span>
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]">
                        {row.label}
                      </span>
                      <span className="text-sm font-medium text-ink transition-colors group-hover:text-rose">
                        {row.value}
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
